/**
 * Darstellungshelfer der Oberfläche.
 *
 * Hier steht bewusst nur Formatierung – keine Fachlogik. Fälligkeiten, Alter
 * und Zählweisen kommen fertig berechnet vom Server.
 */

const WOCHENTAGE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/** ISO-Datum als TT.MM.JJ. */
export function dat(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '—';
  return (
    String(d.getDate()).padStart(2, '0') +
    '.' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '.' +
    String(d.getFullYear()).slice(2)
  );
}

export function wochentag(iso: string): string {
  return WOCHENTAGE[new Date(iso + 'T00:00:00').getDay()] ?? '';
}

/**
 * Zahl mit Tausenderpunkten: 31500 → "31.500".
 *
 * Bewusst von Hand gruppiert statt über toLocaleString('de-AT'): die aktuellen
 * Browser liefern für de-AT ein schmales geschütztes Leerzeichen als Trenner
 * ("31 500"). Das Design verlangt den Punkt, und der Trenner muss in Oberfläche,
 * Server und Mailtext identisch aussehen.
 */
export function gruppiere(n: number): string {
  const gerundet = Math.round(Math.abs(n));
  const ziffern = String(gerundet).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return (n < 0 ? '-' : '') + ziffern;
}

/** Betrag als "24.500 €". */
export function eur(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return gruppiere(n) + ' €';
}

/** Eingabe eines Betrags: nur Ziffern, live mit Tausenderpunkten. */
export function betragFormatieren(eingabe: string): string {
  const roh = String(eingabe).replace(/[^0-9]/g, '');
  if (roh === '') return '';
  return gruppiere(parseInt(roh, 10));
}

export function betragLesen(eingabe: string): number | null {
  const roh = String(eingabe).replace(/[^0-9]/g, '');
  return roh === '' ? null : parseInt(roh, 10);
}

/** Ganze Tage zwischen einem ISO-Datum und dem Bezugstag vom Server. */
export function tageSeit(iso: string, heute: string): number {
  return Math.round(
    (new Date(heute + 'T00:00:00').getTime() - new Date(iso + 'T00:00:00').getTime()) / 86_400_000,
  );
}

/** Aktuelle Uhrzeit als "HH:MM". */
export function jetztUhrzeit(): string {
  const d = new Date();
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}

export function initialen(name: string): string {
  const roh = (name || '')
    .replace(/^(Ing\.|Dr\.|DI|Mag\.|Familie|Zimmerei|Bauträger)\s+/i, '')
    .trim();
  const teile = roh.split(/[\s-]+/).filter(Boolean);
  return ((teile[0]?.[0] ?? '?') + (teile[1]?.[0] ?? '')).toUpperCase();
}

/**
 * Initialen eines Team-Mitglieds – aus dem vollen Namen, alle Bestandteile.
 * Michal Bela → MB, Martin Zihr → MZ, Tibor Poos-Kallinger → TPK.
 * Zwei Buchstaben genügen im Team nicht: Michal und Martin ergäben beide „M".
 * Muss deckungsgleich mit `teamInitialen` im Server bleiben.
 */
export function teamInitialen(anzeigename: string): string {
  const roh = (anzeigename || '')
    .replace(/^(Ing\.|Dr\.|DI|Mag\.|Familie|Zimmerei|Bauträger)\s+/i, '')
    .trim();
  const teile = roh.split(/[\s-]+/).filter(Boolean);
  if (teile.length === 0) return '?';
  return teile
    .map((t) => t[0] ?? '')
    .join('')
    .toUpperCase();
}

export function norm(s: string | null | undefined): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9äöüß@.]/g, '');
}

/** "1 Vorgang" / "3 Vorgänge". */
export function anzahlText(n: number, einzahl: string, mehrzahl: string): string {
  return `${n} ${n === 1 ? einzahl : mehrzahl}`;
}

/** Wörter, die eine Organisation statt einer Privatperson anzeigen. */
const FIRMEN_WORT =
  /\b(gmbh|ges\.?m\.?b\.?h|kg|og|ag|e\.?u\.?|immo|praxis|arztpraxis|ordination|hausverwaltung|bau|fenster|holz|verein|gemeinde|hotel|apotheke)\b/i;

/** Titel, die vor dem Namen stehen und nicht Teil des Namens sind. */
const TITEL = /^(Ing\.|Dr\.|DI|Mag\.)\s+/i;

export interface NameZerlegt {
  typ: 'person' | 'organisation';
  anrede: string;
  titel: string;
  vorname: string;
  nachname: string;
  firma: string;
}

/**
 * Zerlegt einen eingetippten Namen in die Felder der Kundenmaske.
 *
 * Wird gebraucht, wenn aus der Suche heraus ein neuer Kunde angelegt wird: Was
 * schon getippt wurde, soll nicht noch einmal getippt werden müssen.
 *
 * Die Regel ist bewusst dieselbe wie in `kundeAus` am Server (abzug.ts) – wer
 * sie hier ändert, muss sie dort mitändern. Zwei Namen, zwei Ergebnisse wäre
 * schlimmer als eine Regel, die gelegentlich danebenliegt: Die Felder stehen
 * im Formular und lassen sich in zwei Sekunden richtigstellen.
 */
export function nameZerlegen(text: string): NameZerlegt {
  const leer: NameZerlegt = {
    typ: 'person',
    anrede: '',
    titel: '',
    vorname: '',
    nachname: '',
    firma: '',
  };

  // Klammerzusatz und alles hinter dem Komma sind Ort oder Notiz, kein Name.
  const ohneKlammer = (text || '').replace(/\([^)]*\)\s*$/, '').trim();
  let kern = (ohneKlammer.split(',')[0] ?? '').trim().replace(/\s+/g, ' ');
  if (!kern) return leer;

  let anrede = '';
  if (/^Familie\s+/i.test(kern)) {
    anrede = 'Familie';
    kern = kern.replace(/^Familie\s+/i, '').trim();
  }

  let titel = '';
  const titelTreffer = TITEL.exec(kern);
  if (titelTreffer) {
    titel = (titelTreffer[1] ?? '').trim();
    kern = kern.replace(TITEL, '').trim();
  }

  if (!kern) return { ...leer, anrede, titel };

  // Ziffern oder ein Firmenwort: das ist keine Privatperson.
  if (/\d/.test(kern) || FIRMEN_WORT.test(kern)) {
    return { ...leer, typ: 'organisation', firma: kern };
  }

  const woerter = kern.split(' ').filter(Boolean);
  if (woerter.length === 1) {
    return { ...leer, anrede, titel, nachname: woerter[0]! };
  }

  // Bei mehreren Wörtern gilt das letzte als Nachname – „Martin Zihr" ebenso
  // wie „Anna Maria Huber". Umgekehrte Schreibweisen kommen vor, sind aber
  // seltener; korrigiert wird im Formular.
  return {
    ...leer,
    anrede,
    titel,
    vorname: woerter.slice(0, -1).join(' '),
    nachname: woerter[woerter.length - 1]!,
  };
}

/**
 * Sieht das nach einer E-Mail-Adresse aus?
 *
 * Bewusst ohne regulären Ausdruck: Die Escape-Zeichen darin sind eine
 * Fehlerquelle, und eine vollständige Prüfung nach RFC ist ohnehin nicht
 * möglich. Geprüft wird das, was in der Praxis schiefgeht – vergessenes @,
 * fehlende Domainendung, Leerzeichen aus der Zwischenablage.
 *
 * Ob die Adresse wirklich existiert, weiß erst der Mailserver.
 */
export function mailPlausibel(wert: string): boolean {
  const text = wert.trim();
  if (!text || text.includes(' ')) return false;

  const teile = text.split('@');
  if (teile.length !== 2) return false;

  const [vorn, hinten] = teile as [string, string];
  if (!vorn) return false;

  const punkt = hinten.lastIndexOf('.');
  // Domain braucht einen Punkt, davor und dahinter mindestens ein Zeichen.
  return punkt > 0 && punkt < hinten.length - 1;
}
